import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import LanguageToggle from './LanguageToggle';
import { useTranslation } from 'react-i18next';
import {
  Globe, Star, BarChart3, Megaphone, MessageSquare, Search,
  CheckCircle2, ArrowRight, Zap, Shield, Phone,
  TrendingUp, Users, MapPin, Eye,
  Layers, Target, Award, Menu, X,
  PhoneCall, Bot, CreditCard, BrainCircuit, Send, Bell, RotateCcw, ChevronDown
} from 'lucide-react';
import Marquee from 'react-fast-marquee';
import INDUSTRIES from '../data/industries';
import SEOHead, { createOrganizationSchema, createSoftwareSchema, createLocalBusinessSchema, createFAQSchema } from './SEOHead';

const COLORS = {
  primary: '#002FA7',
  primaryHover: '#002080',
  accent: '#FFCC00',
  accentHover: '#E6B800',
};

const SERVICES = [
  'Plumbing', 'HVAC', 'Dental', 'Legal', 'Restaurants', 'Auto Repair',
  'Real Estate', 'Landscaping', 'Roofing', 'Cleaning', 'Chiropractic',
  'Veterinary', 'Accounting', 'Pest Control', 'Salons', 'Fitness',
  'Home Remodeling', 'Insurance', 'Photography', 'Pet Grooming',
  'Electricians', 'Moving', 'Storage', 'Daycare',
];

const FEATURES = [
  { icon: Globe, key: 'listings' },
  { icon: Star, key: 'reputation' },
  { icon: Search, key: 'localSeo' },
  { icon: Megaphone, key: 'social' },
  { icon: Target, key: 'ads' },
  { icon: Eye, key: 'ai' },
  { icon: MessageSquare, key: 'sms' },
  { icon: BarChart3, key: 'reports' },
  { icon: Globe, key: 'website' },
];

const PRICING = [
  {
    id: 'starter',
    nameKey: 'starter',
    allInclusive: 199,
    setupMonthly: { setup: 299, monthly: 149 },
    popular: false,
  },
  {
    id: 'professional',
    nameKey: 'professional',
    allInclusive: 249,
    setupMonthly: { setup: 399, monthly: 199 },
    popular: true,
  },
  {
    id: 'agency',
    nameKey: 'agency',
    allInclusive: 399,
    setupMonthly: { setup: 499, monthly: 299 },
    popular: false,
  },
];

const COMPARISONS = [
  { keyName: 'listings', us: true, others: true, yext: true },
  { keyName: 'reputation', us: true, others: true, yext: true },
  { keyName: 'localSeo', us: true, others: true, yext: false },
  { keyName: 'social', us: true, others: true, yext: false },
  { keyName: 'metaAds', us: true, others: true, yext: false },
  { keyName: 'aiVis', us: true, others: false, yext: false },
  { keyName: 'smsTwoWay', us: true, others: false, yext: false },
  { keyName: 'email', us: true, others: false, yext: false },
  { keyName: 'callTracking', us: true, others: false, yext: false },
  { keyName: 'website', us: true, others: false, yext: false },
  { keyName: 'geofencing', us: true, others: false, yext: false },
  { keyName: 'noContracts', us: true, others: false, yext: false },
  { keyName: 'startingPrice', us: '$149/mo', others: '$500+/mo', yext: '$499+/mo' },
];

const RESULTS = [
  { metric: '70+', keyName: 'directories' },
  { metric: '24/7', keyName: 'callTracking' },
  { metric: '98%', keyName: 'smsOpen' },
  { metric: '$0.049', keyName: 'perText' },
  { metric: '2FA', keyName: 'security' },
  { metric: '1', keyName: 'oneDash' },
];

function FAQAccordion() {
  const { t } = useTranslation('home');
  const [open, setOpen] = useState(null);
  const items = t('faq.items', { returnObjects: true });
  const list = Array.isArray(items) ? items : [];
  return (
    <div className="space-y-3" data-testid="faq-accordion">
      {list.map((item, i) => (
        <div key={i} className="border-2 border-slate-100 rounded-xl overflow-hidden hover:border-slate-200 transition-colors">
          <button
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full text-left px-6 py-4 flex items-center justify-between gap-4"
            data-testid={`faq-q-${i}`}
          >
            <span className="text-sm font-semibold text-slate-900 pr-4">{item.q}</span>
            <ChevronDown className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${open === i ? 'rotate-180' : ''}`} />
          </button>
          {open === i && (
            <div className="px-6 pb-5 -mt-1">
              <p className="text-sm text-slate-600 leading-relaxed">{item.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function LocalBrandingProLanding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingModel, setBillingModel] = useState('all_inclusive');

  // Capture referral code from URL (?ref=UA-XXXXX or ?ref=AFF-XXXXX)
  const refCode = searchParams.get('ref') || '';
  const refParam = refCode ? `&ref=${encodeURIComponent(refCode)}` : '';

  const { t } = useTranslation('home');

  const goToApp = () => navigate(`/signup?plan=professional${refParam}`);
  const goToSignup = (plan) => navigate(`/signup?plan=${plan}${refParam}`);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="lbp-landing">

      <SEOHead
        title="LocalBranding Pro — Local SEO, Listings, Reviews & Geo-Fencing for Small Business"
        description="All-in-one local marketing platform for small businesses. Manage 70+ directory listings, Google Business Profile, review monitoring, local SEO, geo-fencing ads, call tracking, SMS campaigns, and AI visibility — starting at $149/mo. No contracts."
        schema={[
          createOrganizationSchema(),
          createSoftwareSchema(),
          createLocalBusinessSchema(),
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'LocalBranding Pro — Local SEO & Marketing Platform',
            description: 'All-in-one local marketing SaaS for small businesses. Listings, reviews, local SEO, geo-fencing advertising, call tracking, SMS, and AI visibility.',
            url: 'https://localbrandingpro.com',
            mainEntity: {
              '@type': 'SoftwareApplication',
              name: 'LocalBranding Pro',
              applicationCategory: 'BusinessApplication',
              operatingSystem: 'Web',
            }
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: 'Geo-Fencing Advertising for Local Businesses',
            provider: { '@type': 'Organization', name: 'LocalBranding Pro', url: 'https://localbrandingpro.com' },
            description: 'Location-based advertising that targets customers within a set radius of your business. Ads appear on Facebook and Instagram when potential customers are nearby. Setup: $299, Management: $149/mo + 15% of ad spend.',
            serviceType: 'Geo-Fencing Advertising',
            areaServed: { '@type': 'Country', name: 'United States' },
            offers: { '@type': 'Offer', price: '149', priceCurrency: 'USD', description: 'Monthly management fee + $299 setup + 15% of ad spend' }
          },
          createFAQSchema([
            { q: 'What is LocalBranding Pro?', a: 'LocalBranding Pro is an all-in-one local marketing platform that helps small businesses manage directory listings, Google Business Profile, reviews, local SEO, social media, call tracking, SMS campaigns, and AI visibility from one dashboard.' },
            { q: 'How much does LocalBranding Pro cost?', a: 'Plans start at $149/month (Setup + Monthly) or $199/month (All-Inclusive with no setup fee). All plans include a 7-day free trial. No long-term contracts — cancel anytime.' },
            { q: 'What is geo-fencing advertising?', a: 'Geo-fencing creates a virtual perimeter around your business (1-10 mile radius). When potential customers enter this zone, they see your ads on Facebook and Instagram. It drives foot traffic by reaching people when they are physically close to your location.' },
            { q: 'How does geo-fencing work with Facebook?', a: 'We set up a geo-fence around your business location. When someone with a smartphone enters the zone, Meta detects their location and shows your targeted ad in their Facebook or Instagram feed — for example, "Fresh pies baked daily, 0.3 miles away."' },
            { q: 'How does call tracking work?', a: 'We assign unique local tracking phone numbers to each of your marketing channels (website, Google Ads, Facebook, print ads). Calls are forwarded to your real business number while we track the source, record the call, and provide analytics.' },
            { q: 'What directories does LocalBranding Pro sync to?', a: 'We sync your business information across 70+ directories including Google Business Profile, Yelp, Apple Maps, Bing Places, Facebook, Nextdoor, Yellow Pages, BBB, and industry-specific directories.' },
            { q: 'Can I cancel anytime?', a: 'Yes. There are no long-term contracts. You can cancel your subscription at any time. All plans include a 7-day free trial to test the platform before committing.' },
          ]),
        ]}
      />

      {/* HEADER */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-xl bg-white/80 border-b-2 border-slate-100" data-testid="lbp-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: COLORS.primary }}>
              <Layers className="h-4 w-4 text-white" />
            </div>
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>
              LocalBranding Pro
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">{t('nav.features')}</a>
            <a href="#geofencing" className="text-sm font-medium text-purple-600 hover:text-purple-800 transition-colors font-bold">{t('nav.geofencing')}</a>
            <a href="#compare" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">{t('nav.compare')}</a>
            <a href="#pricing" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">{t('nav.pricing')}</a>
            <Link to="/compare" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">{t('nav.vsOthers')}</Link>
            <Link to="/blog" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">{t('nav.industryNews')}</Link>
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <LanguageToggle />
            <button
              onClick={() => navigate('/client/login')}
              className="text-sm font-bold px-4 py-2 rounded-md border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white transition-all"
              data-testid="lbp-login-btn"
            >
              {t('nav.login')}
            </button>
            <button
              onClick={goToApp}
              className="text-sm font-bold px-4 py-2 rounded-md transition-all hover:-translate-y-0.5"
              style={{ backgroundColor: COLORS.accent, color: '#0A0A0A', border: '2px solid transparent' }}
              onMouseEnter={e => { e.target.style.borderColor = '#0A0A0A'; }}
              onMouseLeave={e => { e.target.style.borderColor = 'transparent'; }}
              data-testid="lbp-signup-btn"
            >
              {t('nav.getStarted')}
            </button>
          </div>
          <button className="md:hidden" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="lbp-mobile-menu">
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t px-4 py-4 space-y-3">
            <a href="#features" className="block text-sm font-medium text-slate-700">{t('nav.features')}</a>
            <a href="#compare" className="block text-sm font-medium text-slate-700">{t('nav.compare')}</a>
            <a href="#pricing" className="block text-sm font-medium text-slate-700">{t('nav.pricing')}</a>
            <button onClick={() => navigate('/client/login')} className="w-full text-sm font-bold py-2.5 rounded-md border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white transition-all" data-testid="lbp-mobile-login-btn">
              {t('nav.login')}
            </button>
            <button onClick={goToApp} className="w-full text-sm font-bold py-2.5 rounded-md" style={{ backgroundColor: COLORS.accent }}>
              {t('nav.getStarted')}
            </button>
          </div>
        )}
      </header>

      {/* HERO */}
      <section className="pt-32 pb-20 sm:pt-40 sm:pb-28 relative overflow-hidden" data-testid="lbp-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-6">
                <Award className="h-3.5 w-3.5" style={{ color: COLORS.primary }} />
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.primary }}>{t('hero.badge')}</span>
              </div>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black leading-[1.05] mb-6"
                style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}
              >
                {t('hero.headline')}<br />
                <span style={{ color: COLORS.primary }}>{t('hero.headlineAccent')}</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-lg">
                {t('hero.subheadline')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={goToApp}
                  className="inline-flex items-center justify-center gap-2 text-base font-bold py-3.5 px-7 rounded-md transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  style={{ backgroundColor: COLORS.accent, color: '#0A0A0A' }}
                  data-testid="lbp-hero-cta"
                >
                  {t('hero.ctaPrimary')} <ArrowRight className="h-4 w-4" />
                </button>
                <a
                  href="#compare"
                  className="inline-flex items-center justify-center gap-2 text-base font-bold py-3.5 px-7 rounded-md border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white transition-all"
                  data-testid="lbp-hero-compare"
                >
                  {t('hero.ctaSecondary')}
                </a>
              </div>
              <div className="flex items-center gap-6 mt-8 pt-6 border-t border-slate-200">
                <div className="text-center">
                  <p className="text-2xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>{t('hero.stat1Value')}</p>
                  <p className="text-xs text-slate-500">{t('hero.stat1')}</p>
                </div>
                <div className="w-px h-8 bg-slate-200"></div>
                <div className="text-center">
                  <p className="text-2xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>{t('hero.stat2Value')}</p>
                  <p className="text-xs text-slate-500">{t('hero.stat2')}</p>
                </div>
                <div className="w-px h-8 bg-slate-200"></div>
                <div className="text-center">
                  <p className="text-2xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>{t('hero.stat3Value')}</p>
                  <p className="text-xs text-slate-500">{t('hero.stat3')}</p>
                </div>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="rounded-2xl overflow-hidden shadow-2xl border-2 border-slate-200">
                <img
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80"
                  alt={t('hero.imgAlt')}
                  className="w-full h-[420px] object-cover"
                />
              </div>
              <div className="absolute -left-6 top-16 bg-white rounded-xl shadow-xl border p-4 animate-bounce" style={{ animationDuration: '3s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#DCFCE7' }}>
                    <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">{t('hero.img1Title')}</p>
                    <p className="text-xs text-slate-500">{t('hero.img1Sub')}</p>
                  </div>
                </div>
              </div>
              <div className="absolute -right-4 bottom-20 bg-white rounded-xl shadow-xl border p-4 animate-bounce" style={{ animationDuration: '3.5s', animationDelay: '0.5s' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FEF3C7' }}>
                    <Star className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">{t('hero.img2Title')}</p>
                    <p className="text-xs text-slate-500">{t('hero.img2Sub')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRY MARQUEE */}
      <section className="py-8 border-y-2 border-slate-100 bg-slate-50" data-testid="lbp-industries">
        <Marquee gradient gradientColor="#F4F5F7" speed={40} pauseOnHover>
          {SERVICES.map((s, i) => {
            const ind = INDUSTRIES.find(ind => ind.name.toLowerCase() === s.toLowerCase());
            const Wrapper = ind ? Link : 'span';
            const props = ind ? { to: `/industries/${ind.slug}` } : {};
            const label = t(`industries.${s.toLowerCase().replace(/[^a-z0-9]/g, '_')}`, s);
            return (
              <Wrapper key={i} {...props} className={`mx-8 text-lg sm:text-xl font-bold uppercase tracking-wider whitespace-nowrap ${ind ? 'text-slate-400 hover:text-blue-700 transition-colors' : 'text-slate-300'}`} style={{ fontFamily: "'Outfit', sans-serif" }}>
                {label}
              </Wrapper>
            );
          })}
        </Marquee>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-24 sm:py-32" data-testid="lbp-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: COLORS.primary }}>{t('features.eyebrow')}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>
              {t('features.title')}
            </h2>
            <p className="text-base text-slate-600 mt-4 max-w-xl mx-auto">
              {t('features.subtitle')}
            </p>
          </div>

          {/* Bento Grid — mirrors JDP layout */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-7 bg-white border-2 border-slate-200 rounded-lg p-8 hover:shadow-md transition-all group">
              <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: '#EFF6FF' }}>
                <Globe className="h-6 w-6" style={{ color: COLORS.primary }} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{t('features.listings.title')}</h3>
              <p className="text-slate-600 mb-4">{t('features.listings.desc')}</p>
              <img src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600&h=250&fit=crop&q=80" alt={t('features.imgAlt')} className="w-full h-48 object-cover rounded-lg" />
            </div>
            <div className="md:col-span-5 flex flex-col gap-6">
              <div className="bg-white border-2 border-slate-200 rounded-lg p-8 hover:shadow-md transition-all flex-1">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: '#FEF3C7' }}>
                  <Star className="h-6 w-6 text-yellow-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{t('features.reputation.title')}</h3>
                <p className="text-slate-600">{t('features.reputation.desc')}</p>
              </div>
              <div className="bg-white border-2 border-slate-200 rounded-lg p-8 hover:shadow-md transition-all flex-1">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-5" style={{ backgroundColor: '#F0FDF4' }}>
                  <Search className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{t('features.localSeo.title')}</h3>
                <p className="text-slate-600">{t('features.localSeo.desc')}</p>
              </div>
            </div>
            {FEATURES.filter((_, i) => i >= 3).map((feat) => (
              <div key={feat.key} className="md:col-span-4 bg-white border-2 border-slate-200 rounded-lg p-8 hover:shadow-md transition-all">
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-5 bg-slate-100">
                  <feat.icon className="h-6 w-6 text-slate-700" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{t(`features.list.${feat.key}.title`)}</h3>
                <p className="text-sm text-slate-600">{t(`features.list.${feat.key}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCT SHOWCASE — Detailed Feature Deep-Dives */}
      <section className="py-24 sm:py-32 bg-white" data-testid="lbp-product-showcase">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: COLORS.primary }}>{t('showcase.eyebrow')}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>
              {t('showcase.title')}
            </h2>
            <p className="text-base text-slate-600 mt-4 max-w-2xl mx-auto">{t('showcase.subtitle')}</p>
          </div>

          {/* Feature 1: SMS & Two-Way Texting */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-24" data-testid="feature-sms">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-5" style={{ background: '#F0F9FF', color: '#0369a1' }}>
                <MessageSquare className="h-3.5 w-3.5" /> {t('showcase.smsBadge')}
              </span>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>
                {t('showcase.smsTitle')}
              </h3>
              <p className="text-slate-600 mb-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('showcase.smsP1') }} />
              <p className="text-slate-500 mb-6 text-sm leading-relaxed">
                {t('showcase.smsP2')}
              </p>
              <div className="space-y-3 mb-6">
                {(t('showcase.smsBullets', { returnObjects: true }) || []).map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: '#0369a1' }} />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              <div className="bg-sky-50 border border-sky-200 rounded-lg px-4 py-3">
                <p className="text-xs text-sky-800" dangerouslySetInnerHTML={{ __html: t('showcase.smsPricing') }} />
              </div>
            </div>
            <FeatureSlideshow
              testId="slideshow-sms"
              color="#0369a1"
              slideshowKey="sms"
            />
          </div>

          {/* Feature 2: Call Tracking */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-24" data-testid="feature-call-tracking">
            <FeatureSlideshow
              testId="slideshow-call-tracking"
              color="#7c3aed"
              slideshowKey="callTracking"
            />
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-5" style={{ background: '#F5F3FF', color: '#7c3aed' }}>
                <PhoneCall className="h-3.5 w-3.5" /> {t('showcase.callBadge')}
              </span>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>
                {t('showcase.callTitle')}
              </h3>
              <p className="text-slate-600 mb-4 leading-relaxed">
                {t('showcase.callP1')}
              </p>
              <p className="text-slate-500 mb-6 text-sm leading-relaxed">
                {t('showcase.callP2')}
              </p>
              <div className="space-y-3 mb-6">
                {(t('showcase.callBullets', { returnObjects: true }) || []).map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: '#7c3aed' }} />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-3">
                <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3 flex-1">
                  <p className="text-xs text-purple-900 font-semibold">{t('showcase.callPriceLabel1')}</p>
                  <p className="text-lg font-black text-purple-700" style={{ fontFamily: "'Outfit', sans-serif" }}>$79/mo</p>
                  <p className="text-[10px] text-purple-600">{t('showcase.callPriceSub1')}</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3 flex-1">
                  <p className="text-xs text-purple-900 font-semibold">{t('showcase.callPriceLabel2')}</p>
                  <p className="text-lg font-black text-purple-700" style={{ fontFamily: "'Outfit', sans-serif" }}>$149/mo</p>
                  <p className="text-[10px] text-purple-600">{t('showcase.callPriceSub2')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Feature 3: Reputation & Reviews */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-24" data-testid="feature-reviews">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-5" style={{ background: '#FEF3C7', color: '#b45309' }}>
                <Star className="h-3.5 w-3.5" /> {t('showcase.reviewsBadge')}
              </span>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>
                {t('showcase.reviewsTitle')}
              </h3>
              <p className="text-slate-600 mb-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('showcase.reviewsP1') }} />
              <p className="text-slate-500 mb-6 text-sm leading-relaxed">
                {t('showcase.reviewsP2')}
              </p>
              <div className="space-y-3">
                {(t('showcase.reviewsBullets', { returnObjects: true }) || []).map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: '#b45309' }} />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <FeatureSlideshow
              testId="slideshow-reviews"
              color="#b45309"
              slideshowKey="reviews"
            />
          </div>

          {/* Feature 4: Facebook & Instagram Ads */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-24" data-testid="feature-ads">
            <FeatureSlideshow
              testId="slideshow-ads"
              color="#2563EB"
              slideshowKey="ads"
            />
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-5" style={{ background: '#EFF6FF', color: '#2563EB' }}>
                <Megaphone className="h-3.5 w-3.5" /> {t('showcase.adsBadge')}
              </span>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>
                {t('showcase.adsTitle')}
              </h3>
              <p className="text-slate-600 mb-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('showcase.adsP1') }} />
              <p className="text-slate-500 mb-6 text-sm leading-relaxed">
                {t('showcase.adsP2')}
              </p>
              <div className="space-y-3 mb-6">
                {(t('showcase.adsBullets', { returnObjects: true }) || []).map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: '#2563EB' }} />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                <p className="text-xs text-blue-800" dangerouslySetInnerHTML={{ __html: t('showcase.adsPricing') }} />
              </div>
            </div>
          </div>

          {/* Feature 5: AI Visibility */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-24" data-testid="feature-ai-visibility">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-5" style={{ background: '#FDF2F8', color: '#be185d' }}>
                <BrainCircuit className="h-3.5 w-3.5" /> {t('showcase.aiBadge')}
              </span>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>
                {t('showcase.aiTitle')}
              </h3>
              <p className="text-slate-600 mb-4 leading-relaxed">
                {t('showcase.aiP1')}
              </p>
              <p className="text-slate-500 mb-6 text-sm leading-relaxed">
                {t('showcase.aiP2')}
              </p>
              <div className="space-y-3">
                {(t('showcase.aiBullets', { returnObjects: true }) || []).map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: '#be185d' }} />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <FeatureSlideshow
              testId="slideshow-ai"
              color="#be185d"
              slideshowKey="ai"
            />
          </div>

          {/* Feature 6: Client Dashboard */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-24" data-testid="feature-dashboard">
            <FeatureSlideshow
              testId="slideshow-dashboard"
              color="#16a34a"
              slideshowKey="dashboard"
            />
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-5" style={{ background: '#F0FDF4', color: '#16a34a' }}>
                <BarChart3 className="h-3.5 w-3.5" /> {t('showcase.dashBadge')}
              </span>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>
                {t('showcase.dashTitle')}
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {t('showcase.dashP1')}
              </p>
              <div className="space-y-3">
                {(t('showcase.dashBullets', { returnObjects: true }) || []).map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: '#16a34a' }} />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Feature 7: Local SEO */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-24" data-testid="feature-seo">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-5" style={{ background: '#FEF3C7', color: '#b45309' }}>
                <Search className="h-3.5 w-3.5" /> {t('showcase.seoBadge')}
              </span>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>
                {t('showcase.seoTitle')}
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {t('showcase.seoP1')}
              </p>
              <div className="space-y-3">
                {(t('showcase.seoBullets', { returnObjects: true }) || []).map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: '#b45309' }} />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <FeatureSlideshow
              testId="slideshow-seo"
              color="#b45309"
              slideshowKey="seo"
            />
          </div>

          {/* Feature 8: How It Works */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="bg-white rounded-2xl border-2 border-slate-100 p-8 shadow-sm">
              <div className="space-y-6">
                {[
                  { step: '1', titleKey: 'showcase.step1Title', descKey: 'showcase.step1Desc', color: '#2563EB' },
                  { step: '2', titleKey: 'showcase.step2Title', descKey: 'showcase.step2Desc', color: '#7C3AED' },
                  { step: '3', titleKey: 'showcase.step3Title', descKey: 'showcase.step3Desc', color: '#059669' },
                  { step: '4', titleKey: 'showcase.step4Title', descKey: 'showcase.step4Desc', color: '#dc2626' },
                ].map((s) => (
                  <div key={s.step} className="flex gap-4 items-start">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold" style={{ background: s.color }}>
                      {s.step}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">{t(s.titleKey)}</p>
                      <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{t(s.descKey)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full mb-5" style={{ background: '#EFF6FF', color: '#2563EB' }}>
                <TrendingUp className="h-3.5 w-3.5" /> {t('showcase.stepsBadge')}
              </span>
              <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>
                {t('showcase.stepsTitle')}
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {t('showcase.stepsP1')}
              </p>
              <div className="space-y-3">
                {(t('showcase.stepsBullets', { returnObjects: true }) || []).map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: '#2563EB' }} />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section id="geofencing" className="py-24 sm:py-32 bg-gradient-to-b from-white to-slate-50" data-testid="lbp-geofencing">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: '#7c3aed' }}>{t('geofencing.eyebrow')}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>
              {t('geofencing.title')}
            </h2>
            <p className="text-base text-slate-600 mt-4 max-w-2xl mx-auto">{t('geofencing.subtitle')}</p>
          </div>

          {/* How It Works — Visual Slideshow */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            <div className="bg-white rounded-2xl border-2 border-slate-100 overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
              <div className="h-48 overflow-hidden">
                <img src="https://static.prod-images.emergentagent.com/jobs/f623804d-8eac-4935-b4ff-3986c2c4d488/images/ba0fcddf6a2f7caa064619f537475206f6fc89ebad83a334b4f9f8238eb578e8.png" alt={t('geofencing.imgAlt1')} className="w-full h-full object-cover" />
              </div>
              <div className="p-5">
                <div className="inline-flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-bold mb-3" style={{ backgroundColor: '#7c3aed' }}>1</div>
                <h3 className="text-base font-bold text-slate-900 mb-1">{t('geofencing.step1Title')}</h3>
                <p className="text-sm text-slate-600">{t('geofencing.step1Desc')}</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border-2 border-slate-100 overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
              <div className="h-48 overflow-hidden">
                <img src="https://static.prod-images.emergentagent.com/jobs/f623804d-8eac-4935-b4ff-3986c2c4d488/images/11b08294eba270d8fba07e03ebde42e5f6d8533ad9a221d46d5963ac90ae115a.png" alt={t('geofencing.imgAlt2')} className="w-full h-full object-cover" />
              </div>
              <div className="p-5">
                <div className="inline-flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-bold mb-3" style={{ backgroundColor: '#7c3aed' }}>2</div>
                <h3 className="text-base font-bold text-slate-900 mb-1">{t('geofencing.step2Title')}</h3>
                <p className="text-sm text-slate-600">{t('geofencing.step2Desc')}</p>
              </div>
            </div>
            <div className="bg-white rounded-2xl border-2 border-slate-100 overflow-hidden shadow-sm hover:shadow-lg transition-shadow">
              <div className="h-48 overflow-hidden">
                <img src="https://static.prod-images.emergentagent.com/jobs/f623804d-8eac-4935-b4ff-3986c2c4d488/images/17398b96c0c05e0d6fd00209e472279591e56762f2ef100a29d236a974729917.png" alt={t('geofencing.imgAlt3')} className="w-full h-full object-cover" />
              </div>
              <div className="p-5">
                <div className="inline-flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-bold mb-3" style={{ backgroundColor: '#7c3aed' }}>3</div>
                <h3 className="text-base font-bold text-slate-900 mb-1">{t('geofencing.step3Title')}</h3>
                <p className="text-sm text-slate-600">{t('geofencing.step3Desc')}</p>
              </div>
            </div>
          </div>

          {/* ROI Calculator */}
          <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-sm p-8 mb-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{t('geofencing.exampleTitle')}</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{t('geofencing.ex1Title')}</p>
                      <p className="text-xs text-slate-500">{t('geofencing.ex1Sub')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center flex-shrink-0">
                      <Eye className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{t('geofencing.ex2Title')}</p>
                      <p className="text-xs text-slate-500">{t('geofencing.ex2Sub')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{t('geofencing.ex3Title')}</p>
                      <p className="text-xs text-slate-500">{t('geofencing.ex3Sub')}</p>
                    </div>
                  </div>
                </div>
                <div className="mt-6 bg-purple-50 rounded-xl p-4">
                  <p className="text-sm text-purple-900" dangerouslySetInnerHTML={{ __html: t('geofencing.roiBox') }} />
                </div>
              </div>
              <div className="rounded-xl overflow-hidden border border-slate-100">
                <img src="https://static.prod-images.emergentagent.com/jobs/f623804d-8eac-4935-b4ff-3986c2c4d488/images/3f8e860c04b356b383da29ffac18e51bad30011dd50ab439ba18be28def4439e.png" alt={t('geofencing.imgAltRoi')} className="w-full" />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="bg-gradient-to-br from-purple-700 to-indigo-800 rounded-2xl p-8 sm:p-10 text-center">
            <h3 className="text-xl sm:text-2xl font-black text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{t('geofencing.pricingTitle')}</h3>
            <p className="text-purple-200 text-sm mb-6">{t('geofencing.pricingSub')}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mx-auto mb-8">
              <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                <p className="text-2xl font-black text-white">$299</p>
                <p className="text-xs text-purple-200">{t('geofencing.p1')}</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                <p className="text-2xl font-black text-white">$149</p>
                <p className="text-xs text-purple-200">{t('geofencing.p2')}</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                <p className="text-2xl font-black text-white">15%</p>
                <p className="text-xs text-purple-200">{t('geofencing.p3')}</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
                <p className="text-2xl font-black text-white">{t('geofencing.p4Value')}</p>
                <p className="text-xs text-purple-200">{t('geofencing.p4')}</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/signup?plan=geofencing')}
              className="px-8 py-3 bg-white text-purple-700 font-bold text-sm rounded-md hover:bg-purple-50 transition-all hover:-translate-y-0.5"
              data-testid="geofencing-cta"
            >
              {t('geofencing.cta')}
            </button>
            <p className="text-xs text-purple-300 mt-4">{t('geofencing.ctaSub')}</p>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section id="compare" className="py-24 sm:py-32 bg-slate-50 border-y-2 border-slate-100" data-testid="lbp-compare">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: COLORS.primary }}>{t('compare.eyebrow')}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>
              {t('compare.title')}
            </h2>
            <p className="text-base text-slate-600 mt-4">{t('compare.subtitle')}</p>
          </div>
          <div className="rounded-lg border-2 border-slate-200 overflow-hidden bg-white">
            <table className="w-full">
              <thead>
                <tr className="bg-[#0A0A0A]">
                  <th className="text-left text-sm font-semibold text-white/70 px-6 py-4">{t('compare.thFeature')}</th>
                  <th className="text-center text-sm font-bold px-4 py-4" style={{ color: COLORS.accent }}>{t('compare.thUs')}</th>
                  <th className="text-center text-sm font-medium text-white/50 px-4 py-4">{t('compare.thOthers')}</th>
                  <th className="text-center text-sm font-medium text-white/50 px-4 py-4">{t('compare.thYext')}</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISONS.map((row, i) => (
                  <tr key={row.keyName} className={i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                    <td className="px-6 py-3.5 text-sm font-medium text-slate-800">{t(`compare.rows.${row.keyName}`)}</td>
                    <td className="text-center px-4 py-3.5">
                      {typeof row.us === 'boolean' ? (
                        row.us ? <CheckCircle2 className="h-5 w-5 mx-auto" style={{ color: COLORS.primary }} /> : <span className="text-slate-300">&mdash;</span>
                      ) : row.keyName === 'startingPrice' ? (
                        <span className="text-sm font-bold" style={{ color: COLORS.primary }}>
                          {billingModel === 'all_inclusive' ? '$199/mo' : '$149/mo'}
                        </span>
                      ) : <span className="text-sm font-bold" style={{ color: COLORS.primary }}>{row.us}</span>}
                    </td>
                    <td className="text-center px-4 py-3.5">
                      {typeof row.others === 'boolean' ? (
                        row.others ? <CheckCircle2 className="h-5 w-5 text-slate-400 mx-auto" /> : <span className="text-slate-300">&mdash;</span>
                      ) : <span className="text-sm text-slate-500">{row.others}</span>}
                    </td>
                    <td className="text-center px-4 py-3.5">
                      {typeof row.yext === 'boolean' ? (
                        row.yext ? <CheckCircle2 className="h-5 w-5 text-slate-400 mx-auto" /> : <span className="text-slate-300">&mdash;</span>
                      ) : <span className="text-sm text-slate-500">{row.yext}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-center text-xs text-slate-400 mt-4">
            {billingModel === 'setup_monthly' ? t('compare.footnoteSetup') : t('compare.footnoteAllInc')}{' '}
            <a href="#pricing" className="underline" style={{ color: COLORS.primary }}>{t('compare.seePricing')}</a>
          </p>
        </div>
      </section>

      {/* RESULTS */}
      <section className="py-24 sm:py-32" data-testid="lbp-results">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">{t('results.eyebrow')}</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-12" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>
            {t('results.title')}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {RESULTS.map((r) => (
              <div key={r.keyName} className="bg-white border-2 border-slate-200 rounded-lg p-8 text-center">
                <p className="text-4xl font-black mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: COLORS.primary }}>{r.metric}</p>
                <p className="font-bold text-slate-900 text-sm mb-2">{t(`results.items.${r.keyName}.label`)}</p>
                <p className="text-sm text-slate-500 leading-relaxed">{t(`results.items.${r.keyName}.desc`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 sm:py-32 bg-slate-50 border-y-2 border-slate-100" data-testid="lbp-pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: COLORS.primary }}>{t('pricing.eyebrow')}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>
              {t('pricing.title')}
            </h2>
            <p className="text-base text-slate-600 mt-4">{t('pricing.subtitle')}</p>

            {/* Billing Toggle */}
            <div className="mt-8 inline-flex items-center gap-1 p-1 rounded-full bg-white border-2 border-slate-200 shadow-sm" data-testid="billing-toggle">
              <button
                onClick={() => setBillingModel('all_inclusive')}
                className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${billingModel === 'all_inclusive' ? 'text-white shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                style={billingModel === 'all_inclusive' ? { backgroundColor: COLORS.primary } : {}}
                data-testid="billing-all-inclusive"
              >
                {t('pricing.togglePill1')}
              </button>
              <button
                onClick={() => setBillingModel('setup_monthly')}
                className={`px-5 py-2 rounded-full text-sm font-bold transition-all ${billingModel === 'setup_monthly' ? 'text-white shadow-md' : 'text-slate-500 hover:text-slate-700'}`}
                style={billingModel === 'setup_monthly' ? { backgroundColor: COLORS.primary } : {}}
                data-testid="billing-setup-monthly"
              >
                {t('pricing.togglePill2')}
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              {billingModel === 'all_inclusive' ? t('pricing.toggleSub1') : t('pricing.toggleSub2')}
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {PRICING.map((plan, i) => {
              const displayPrice = billingModel === 'all_inclusive' ? plan.allInclusive : plan.setupMonthly.monthly;
              const setupFee = billingModel === 'setup_monthly' ? plan.setupMonthly.setup : 0;
              const features = t(`pricing.${plan.nameKey}.features`, { returnObjects: true }) || [];
              return (
                <div
                  key={i}
                  className={`rounded-lg p-8 relative ${plan.popular ? 'border-2 shadow-xl' : 'bg-white border-2 border-slate-200'}`}
                  style={plan.popular ? { borderColor: COLORS.primary } : {}}
                  data-testid={`lbp-pricing-${plan.nameKey}`}
                >
                  {plan.popular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: COLORS.primary }}>
                      {billingModel === 'all_inclusive' ? t('pricing.mostPopular') : t('pricing.bestValue')}
                    </div>
                  )}
                  <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{t(`pricing.${plan.nameKey}.name`)}</h3>
                  <p className="text-sm text-slate-500 mb-6">{t(`pricing.${plan.nameKey}.desc`)}</p>
                  <div className="mb-2">
                    <span className="text-4xl font-black text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>${displayPrice}</span>
                    <span className="text-sm text-slate-500">{t('pricing.perMo')}</span>
                  </div>
                  {setupFee > 0 && (
                    <p className="text-xs font-semibold mb-4" style={{ color: COLORS.primary }}>{t('pricing.setupOneTime', { fee: setupFee })}</p>
                  )}
                  {billingModel === 'all_inclusive' && (
                    <p className="text-[11px] text-slate-400 mb-4">{t('pricing.noSetupFee')}</p>
                  )}
                  <button
                    onClick={() => goToSignup(plan.id)}
                    className={`w-full py-3 rounded-md font-bold text-sm transition-all hover:-translate-y-0.5 mb-8 ${
                      plan.popular
                        ? 'text-white hover:opacity-90'
                        : 'border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white'
                    }`}
                    style={plan.popular ? { backgroundColor: COLORS.primary } : {}}
                    data-testid={`pricing-cta-${plan.id}`}
                  >
                    {t(`pricing.${plan.nameKey}.cta`)}
                  </button>
                  <ul className="space-y-3">
                    {features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2.5">
                        <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: COLORS.primary }} />
                        <span className="text-sm text-slate-700">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          <p className="text-center text-xs text-slate-400 mt-6">{t('pricing.footnote')}</p>
        </div>
      </section>

      {/* JDP CROSS-REFERRAL SECTION */}
      <section className="py-16 sm:py-20 bg-white border-b-2 border-slate-100" data-testid="lbp-jdp-referral">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border-2 border-slate-200 overflow-hidden">
            <div className="grid md:grid-cols-2">
              <div className="p-8 sm:p-10 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-5 w-fit">
                  <Zap className="h-3.5 w-3.5" style={{ color: COLORS.primary }} />
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.primary }}>{t('jdp.eyebrow')}</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-black mb-3" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>
                  {t('jdp.title')}
                </h3>
                <p className="text-slate-600 mb-6 leading-relaxed" dangerouslySetInnerHTML={{ __html: t('jdp.desc') }} />
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href="https://jobdispatchpro.com" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 text-sm font-bold py-3 px-6 rounded-md transition-all hover:-translate-y-0.5 hover:shadow-lg"
                    style={{ backgroundColor: COLORS.accent, color: '#0A0A0A' }}
                    data-testid="lbp-jdp-cta"
                  >
                    {t('jdp.cta')} <ArrowRight className="h-4 w-4" />
                  </a>
                  <a
                    href="https://jobdispatchpro.com" target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 text-sm font-bold py-3 px-6 rounded-md border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white transition-all"
                    data-testid="lbp-jdp-demo"
                  >
                    {t('jdp.ctaDemo')}
                  </a>
                </div>
              </div>
              <div className="bg-slate-50 p-8 sm:p-10 flex flex-col justify-center gap-5">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: COLORS.primary }}>
                    <Zap className="h-4 w-4 text-white" />
                  </div>
                  <span className="text-lg font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>{t('jdp.brand')}</span>
                </div>
                {[
                  { icon: Users, key: 'f1' },
                  { icon: MapPin, key: 'f2' },
                  { icon: Shield, key: 'f3' },
                  { icon: BarChart3, key: 'f4' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: COLORS.primary }} />
                    <span className="text-sm text-slate-700">{t(`jdp.${item.key}`)}</span>
                  </div>
                ))}
                <p className="text-xs text-slate-500 pt-2 border-t border-slate-200">
                  {t('jdp.footer')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section id="faq" className="py-24 sm:py-32 bg-white" data-testid="lbp-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-3" style={{ color: COLORS.primary }}>{t('faq.eyebrow')}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: '#0A0A0A' }}>
              {t('faq.title')}
            </h2>
          </div>
          <FAQAccordion />
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 sm:py-32" style={{ backgroundColor: COLORS.primary }} data-testid="lbp-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-white mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {t('ctaBanner.title')}
          </h2>
          <p className="text-lg text-blue-200 mb-8 max-w-2xl mx-auto">
            {t('ctaBanner.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={goToApp}
              className="inline-flex items-center justify-center gap-2 text-base font-bold py-3.5 px-8 rounded-md transition-all hover:-translate-y-0.5 hover:shadow-lg"
              style={{ backgroundColor: COLORS.accent, color: '#0A0A0A' }}
              data-testid="lbp-cta-trial-btn"
            >
              {t('ctaBanner.primary')} <ArrowRight className="h-4 w-4" />
            </button>
            <a
              href="https://jobdispatchpro.com" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 text-base font-bold py-3.5 px-8 rounded-md border-2 border-white text-white hover:bg-white hover:text-slate-900 transition-all"
              data-testid="lbp-cta-jdp-link"
            >
              <Zap className="h-4 w-4" /> {t('ctaBanner.secondary')}
            </a>
          </div>
          <p className="text-sm text-blue-300/60 mt-6">{t('ctaBanner.footnote')}</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-16 sm:py-24 bg-[#0A0A0A] text-white" data-testid="lbp-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: COLORS.primary }}>
                  <Layers className="h-4 w-4 text-white" />
                </div>
                <span className="text-lg font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>LocalBranding Pro</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                {t('footer.tagline')}
              </p>
            </div>
            <div>
              <h4 className="font-bold text-sm mb-4 uppercase tracking-wider text-slate-300">{t('footer.product')}</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#features" className="hover:text-white transition-colors">{t('footer.linkFeatures')}</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">{t('footer.linkPricing')}</a></li>
                <li><a href="#compare" className="hover:text-white transition-colors">{t('footer.linkCompare')}</a></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">{t('footer.linkBlog')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-sm mb-4 uppercase tracking-wider text-slate-300">{t('footer.industries')}</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                {INDUSTRIES.slice(0, 8).map(ind => (
                  <li key={ind.slug}><Link to={`/industries/${ind.slug}`} className="hover:text-white transition-colors">{t(`industries.${ind.name.toLowerCase().replace(/[^a-z0-9]/g, '_')}`, ind.name)}</Link></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-sm mb-4 uppercase tracking-wider text-slate-300">{t('footer.ourProducts')}</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="https://jobdispatchpro.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">{t('footer.linkJDP')}</a></li>
                <li><a href="https://web-techservices.com" className="hover:text-white transition-colors">{t('footer.linkWTS')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-sm mb-4 uppercase tracking-wider text-slate-300">{t('footer.support')}</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="mailto:support@localbrandingpro.com" className="hover:text-white transition-colors">{t('footer.contact')}</a></li>
                <li><Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">{t('footer.terms')}</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">{t('footer.privacy')}</Link></li>
                <li><Link to="/sms-compliance" className="hover:text-white transition-colors" data-testid="footer-sms-compliance-link">{t('footer.smsCompliance')}</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-500">&copy; {new Date().getFullYear()} LocalBranding Pro. {t('footer.copyright')}</p>
            <div className="flex flex-col items-center md:items-end gap-1">
              <p className="text-sm text-slate-500" dangerouslySetInnerHTML={{ __html: t('footer.wts') }} />
              <a href="/admin/login" className="text-slate-600 hover:text-slate-400 text-[11px] transition-colors" data-testid="footer-admin-login">{t('footer.adminLogin')}</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}


function FeatureSlideshow({ testId, color, slideshowKey }) {
  const { t } = useTranslation('home');
  const slides = t(`slideshows.${slideshowKey}`, { returnObjects: true });
  const list = Array.isArray(slides) ? slides : [];
  const total = list.length || 1;
  const [activeSlide, setActiveSlide] = useState(0);

  const advance = useCallback(() => {
    setActiveSlide(prev => (prev + 1) % total);
  }, [total]);

  useEffect(() => {
    const timer = setInterval(advance, 4000);
    return () => clearInterval(timer);
  }, [advance]);

  if (!list.length) return null;
  const slide = list[Math.min(activeSlide, list.length - 1)];
  const lightBg = color + '08';
  const medBg = color + '15';

  return (
    <div className="rounded-xl overflow-hidden border-2 border-slate-200 shadow-lg bg-white" data-testid={testId} role="region" aria-label={`${list[0]?.title || 'Feature'} slideshow`}>
      <div className="aspect-video flex flex-col relative select-none">
        {/* Slide header */}
        <div className="px-6 pt-5 pb-3 border-b border-slate-100" style={{ background: lightBg }}>
          <div className="flex items-center justify-between mb-1">
            <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color }}>{slide.title}</p>
            <span className="text-[10px] text-slate-400 font-medium">{activeSlide + 1} / {total}</span>
          </div>
          <p className="text-xs text-slate-500">{slide.subtitle}</p>
        </div>

        {/* Slide content */}
        <div className="flex-1 px-6 py-5 flex flex-col justify-center" style={{ background: `linear-gradient(to bottom, ${lightBg}, white)` }}>
          <div className="space-y-3">
            {(slide.items || []).map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-4 py-3 rounded-lg border transition-all"
                style={{ borderColor: medBg, background: i === 0 ? medBg : 'white' }}
              >
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold"
                  style={{ backgroundColor: color }}
                >
                  {i + 1}
                </div>
                <span className="text-sm text-slate-700 font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation dots */}
        <div className="px-6 pb-4 flex items-center justify-center gap-2">
          {list.map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveSlide(i)}
              className="transition-all rounded-full"
              style={{
                width: i === activeSlide ? 24 : 8,
                height: 8,
                backgroundColor: i === activeSlide ? color : '#e2e8f0',
              }}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
